import { PointerEvent as ReactPointerEvent, useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

// Controle virtual translúcido. Cada botão dispara KeyboardEvent
// no window — o Phaser InputSystem já escuta a mesma fila, então
// teclado físico e gamepad virtual coexistem sem mudanças no game code.
//
// Mobile/WebView:
// - auto-hide quando o ponteiro primário é "fine" (mouse desktop);
// - safe-area-inset para notch e barra de gestos;
// - haptic feedback opcional via navigator.vibrate quando disponível;
// - touchAction:none + pointer capture pra evitar scroll/longpress nativos.

interface KeyBinding {
  code: string
  keyCode: number
}

const KEYS = {
  up: { code: 'ArrowUp', keyCode: 38 },
  down: { code: 'ArrowDown', keyCode: 40 },
  left: { code: 'ArrowLeft', keyCode: 37 },
  right: { code: 'ArrowRight', keyCode: 39 },
  a: { code: 'KeyZ', keyCode: 90 }, // Martelo (↓+A = Rasteira)
  b: { code: 'KeyX', keyCode: 88 }, // Benção
  x: { code: 'KeyC', keyCode: 67 }, // Esquiva (↓+X = Aú)
  y: { code: 'Space', keyCode: 32 }, // Especial
  l: { code: 'ShiftLeft', keyCode: 16 }, // Defesa
  l3: { code: 'Digit1', keyCode: 49 },
  r3: { code: 'Digit2', keyCode: 50 },
} as const satisfies Record<string, KeyBinding>

function fireKey(type: 'keydown' | 'keyup', key: KeyBinding) {
  const ev = new KeyboardEvent(type, {
    code: key.code,
    key: key.code,
    keyCode: key.keyCode,
    which: key.keyCode,
    bubbles: true,
    cancelable: true,
  })
  window.dispatchEvent(ev)
}

function vibrate(ms: number) {
  if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
    try { navigator.vibrate(ms) } catch { /* alguns WebViews bloqueiam; ignorar */ }
  }
}

function detectTouch(): boolean {
  if (typeof window === 'undefined') return false
  return (
    window.matchMedia?.('(pointer: coarse)').matches ||
    'ontouchstart' in window ||
    (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0)
  )
}

export default function VirtualGamepad() {
  const [showPad, setShowPad] = useState(detectTouch)
  const navigate = useNavigate()

  useEffect(() => {
    if (typeof window === 'undefined') return
    const mq = window.matchMedia?.('(pointer: coarse)')
    if (!mq) return
    const handler = (e: MediaQueryListEvent) => setShowPad(e.matches || 'ontouchstart' in window)
    mq.addEventListener?.('change', handler)
    return () => mq.removeEventListener?.('change', handler)
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return
    ;(window as any).virtualGamepadActive = showPad
    return () => {
      ;(window as any).virtualGamepadActive = false
    }
  }, [showPad])

  if (!showPad) return null

  return (
    <div style={styles.overlay}>
      <DPad />
      <ActionPad />
      
      {/* Home Button (entre L3 e R3, centralizado) */}
      <button 
        style={styles.homeBtn} 
        onClick={() => navigate('/menu')}
        aria-label="Voltar para o Mapa"
      >
        <HomeIcon />
      </button>

      <PadButton
        binding={KEYS.l}
        label="L"
        sub="Defesa"
        style={{ ...styles.shoulderBtn, left: 'max(20px, env(safe-area-inset-left))' }}
      />

      <PadButton
        binding={KEYS.l3}
        label="L3"
        style={{ ...styles.stickBtn, left: 'calc(max(20px, env(safe-area-inset-left)) + 90px)' }}
      />

      <PadButton
        binding={KEYS.r3}
        label="R3"
        style={{ ...styles.stickBtn, right: 'calc(max(20px, env(safe-area-inset-right)) + 90px)' }}
      />
    </div>
  )
}

// Posicionamento em PERCENTAGEM do wrap. Permite o pad escalar via clamp()
// no wrap sem quebrar layout. 0% / 35% / 70% com botões de 30% gera
// gap de 5% entre pétalas do diamante (~10 px num wrap de 200).
const DIAMOND = {
  size: '30%',
  pos0: '0%',
  posMid: '35%',
  posEnd: '70%',
} as const

function DPad() {
  return (
    <div style={styles.dpadWrap}>
      <PadButton binding={KEYS.up}    label="↑" style={{ ...styles.dpadBtn, top: DIAMOND.pos0,   left: DIAMOND.posMid }} />
      <PadButton binding={KEYS.left}  label="←" style={{ ...styles.dpadBtn, top: DIAMOND.posMid, left: DIAMOND.pos0   }} />
      <PadButton binding={KEYS.right} label="→" style={{ ...styles.dpadBtn, top: DIAMOND.posMid, left: DIAMOND.posEnd }} />
      <PadButton binding={KEYS.down}  label="↓" style={{ ...styles.dpadBtn, top: DIAMOND.posEnd, left: DIAMOND.posMid }} />
    </div>
  )
}

function ActionPad() {
  return (
    <div style={styles.actionWrap}>
      <PadButton binding={KEYS.y} label="Y" sub="Especial" color="#E5B055" style={{ ...styles.actBtn, top: DIAMOND.pos0,   left: DIAMOND.posMid }} />
      <PadButton binding={KEYS.x} label="X" sub="Esquiva"  color="#3A6E8C" style={{ ...styles.actBtn, top: DIAMOND.posMid, left: DIAMOND.pos0   }} />
      <PadButton binding={KEYS.b} label="B" sub="Benção"   color="#C76C3C" style={{ ...styles.actBtn, top: DIAMOND.posMid, left: DIAMOND.posEnd }} />
      <PadButton binding={KEYS.a} label="A" sub="Martelo"  color="#2F5D3A" style={{ ...styles.actBtn, top: DIAMOND.posEnd, left: DIAMOND.posMid }} />
    </div>
  )
}

interface PadButtonProps {
  binding: KeyBinding
  label: string
  sub?: string
  color?: string
  style?: React.CSSProperties
}

function PadButton({ binding, label, sub, color, style }: PadButtonProps) {
  const onDown = useCallback(
    (e: ReactPointerEvent<HTMLButtonElement>) => {
      e.preventDefault()
      e.currentTarget.setPointerCapture?.(e.pointerId)
      e.currentTarget.dataset.pressed = '1'
      vibrate(8)
      fireKey('keydown', binding)
    },
    [binding],
  )
  const onUp = useCallback(
    (e: ReactPointerEvent<HTMLButtonElement>) => {
      if (e.currentTarget.dataset.pressed !== '1') return
      e.currentTarget.dataset.pressed = '0'
      fireKey('keyup', binding)
    },
    [binding],
  )

  return (
    <button
      type="button"
      onPointerDown={onDown}
      onPointerUp={onUp}
      onPointerCancel={onUp}
      onPointerLeave={onUp}
      onContextMenu={(e) => e.preventDefault()}
      style={{
        ...styles.btnBase,
        background: color ? `${color}66` : 'rgba(242, 233, 216, 0.18)',
        borderColor: color ?? 'rgba(242, 233, 216, 0.5)',
        ...style,
      }}
    >
      <span style={styles.btnLabel}>{label}</span>
      {sub && <span style={styles.btnSub}>{sub}</span>}
    </button>
  )
}

function HomeIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
      <polyline points="9 22 9 12 15 12 15 22"></polyline>
    </svg>
  )
}

// Tamanho do pad responsivo. Em telas largas chega a 200 px; em estreitas
// (~360 px) escala para ~160 px. Mantém 48 px+ de área de toque (30% × 160 = 48).
const PAD_SIZE = 'clamp(160px, 32vmin, 200px)'

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none', // Filhos com `auto` interceptam
    userSelect: 'none',
    WebkitUserSelect: 'none',
    touchAction: 'none',
  },
  dpadWrap: {
    position: 'absolute',
    bottom: 'max(28px, env(safe-area-inset-bottom))',
    left: 'max(20px, env(safe-area-inset-left))',
    width: PAD_SIZE,
    height: PAD_SIZE,
    pointerEvents: 'auto',
  },
  dpadBtn: {
    position: 'absolute',
    width: '30%',
    height: '30%',
    fontSize: 'clamp(22px, 5.5vmin, 30px)',
  },
  actionWrap: {
    position: 'absolute',
    bottom: 'max(28px, env(safe-area-inset-bottom))',
    right: 'max(20px, env(safe-area-inset-right))',
    width: PAD_SIZE,
    height: PAD_SIZE,
    pointerEvents: 'auto',
  },
  actBtn: {
    position: 'absolute',
    width: '30%',
    height: '30%',
    fontSize: 'clamp(18px, 4vmin, 22px)',
  },
  // Shoulder ancorado relativo ao dpad: dpadBottom + dpadHeight + 8px de gap.
  shoulderBtn: {
    position: 'absolute',
    bottom: 'calc(max(28px, env(safe-area-inset-bottom)) + clamp(160px, 32vmin, 200px) + 8px)',
    width: 76,
    height: 44,
    fontSize: 14,
    pointerEvents: 'auto',
  },
  stickBtn: {
    position: 'absolute',
    bottom: 'calc(max(28px, env(safe-area-inset-bottom)) + clamp(160px, 32vmin, 200px) + 8px)',
    width: 44,
    height: 44,
    fontSize: 12,
    pointerEvents: 'auto',
  },
  homeBtn: {
    position: 'absolute',
    bottom: 'calc(max(28px, env(safe-area-inset-bottom)) + clamp(160px, 32vmin, 200px) - 8px)',
    left: '50%',
    transform: 'translateX(-50%)',
    width: 44,
    height: 44,
    borderRadius: '50%',
    background: 'rgba(242, 233, 216, 0.18)',
    border: '1px solid rgba(242, 233, 216, 0.5)',
    color: '#F2E9D8',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    pointerEvents: 'auto',
    zIndex: 100,
    opacity: 0.6,
    backdropFilter: 'blur(2px)',
    padding: 0,
  },
  btnBase: {
    color: '#F2E9D8',
    border: '2px solid',
    borderRadius: '50%',
    fontFamily: 'var(--font-body, sans-serif)',
    fontWeight: 700,
    opacity: 0.6,
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
    backdropFilter: 'blur(2px)',
    transition: 'opacity 100ms ease, transform 60ms ease',
    touchAction: 'none',
    WebkitTapHighlightColor: 'transparent',
    padding: 0,
  },
  btnLabel: {
    fontSize: '1.1em',
    textShadow: '1px 1px 2px rgba(0,0,0,0.85)',
  },
  btnSub: {
    fontSize: '0.5em',
    opacity: 0.85,
    marginTop: 2,
    fontWeight: 500,
    letterSpacing: '0.04em',
  },
}
